import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { RequiresActionSearchParams } from 'components/clinical-data/ClinicalDataRecordRequiresAction';
import DatePicker from 'components/final-form/DatePicker';
import StyledActionButton from 'components/layout/StyledActionButton';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { useRef } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Dropdown, Icon, IconGroup } from 'semantic-ui-react';
import styled from 'styled-components';
import { toIsoDateString } from 'utils/dateUtils';
import { noop } from 'utils/functionUtils';

const StyledForm = styled.form`
  padding: 0 0 1rem;

  .form-box {
    display: flex;
    flex-direction: row;

    .group-separator {
      border-left: 1px dotted #aaa;
      margin: 0 0.9rem;
    }

    .label, .date-separator {
      line-height: 2.1rem;
    }

    .date-separator {
      padding: 0 0.375rem;
      line-height: 2.1rem;
      color: #aaa;
    }

    .label {
      padding-right: 0.7rem;
    }

    .value {
      display: flex;
      flex-direction: row;
    }

    .field {
      margin-right: 0.5rem;
    }

    .value.date {
      .field {
        width: 7.7rem;
      }
    }

    .time-frame-dropdown {
      line-height: 2rem;
    }

    .toggles {
      padding-top: 0.1rem;
    }

    .icon-group.large {
      margin-bottom: -0.1rem;
      font-size: 1.3rem;

      .negation {
        color: rgba(0, 0, 0, .275);

        &.blue {
          color: rgba(33, 133, 208, 0.375) !important;
        }
      }

      .negated-icon {
        font-size: 1.2rem;
        margin-left: -0.15rem;
        margin-top: 0.05rem;
      }
    }
  }

`;


interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  className?: string;
  displayRequiresAction: boolean;
  searchParams: RequiresActionSearchParams;
  setSearchParams: (params: Partial<RequiresActionSearchParams>) => void;
  doSearch: (params: RequiresActionSearchParams) => void;
}

const RequiresActionSearchForm = (props: Props) => {

  const {
    getMessage
  } = props;

  const initialValues = useRef(props.searchParams);

  const handleChange = ({values}: FormState, form: FormApi) => {

    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }

    doSearch(form.getState().values as RequiresActionSearchParams);
  };

  const doSearch = (values: RequiresActionSearchParams) => {
    props.setSearchParams(values);
    props.doSearch(values);
  };

  const onEnterKeyPressed = (e: any, values: RequiresActionSearchParams) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      doSearch(values)
    }
  };

  const timeFrameOptions = [
    {
      key: 'until_today',
      text: getMessage("until_today"),
      value: 'until_today',
      content: getMessage("until_today")
    },
    {
      key: 'today',
      text: getMessage("today"),
      value: 'today',
      content: getMessage("today")
    },
    {
      key: 'past_week',
      text: getMessage("past_week"),
      value: 'past_week',
      content: getMessage("past_week")
    },
    {
      key: 'past_month',
      text: getMessage("past_month"),
      value: 'past_month',
      content: getMessage("past_month")
    },
    {
      key: 'past_3_months',
      text: getMessage("past_3_months"),
      value: 'past_3_months',
      content: getMessage("past_3_months")
    },
    {
      key: 'past_6_months',
      text: getMessage("past_6_months"),
      value: 'past_6_months',
      content: getMessage("past_6_months")
    },
    {
      key: 'past_year',
      text: getMessage("past_year"),
      value: 'past_year',
      content: getMessage("past_year")
    },
    {
      key: 'all',
      text: getMessage("all"),
      value: 'all',
      content: getMessage("all")
    }
  ];

  const onTimeFrameChange = (value: string, form: FormApi) => {

    form.batch(() => {
      form.change("timeFrame", value);
      if (value === "until_today") {

        form.change("fromDate", undefined);
        form.change("toDate", toIsoDateString(moment()));
      } else if (value === "today") {
        form.change("fromDate", toIsoDateString(moment()));
        form.change("toDate", toIsoDateString(moment()));
      } else if (value === "past_week") {
        form.change("fromDate", toIsoDateString(moment().subtract(7, "days")));
        form.change("toDate", toIsoDateString(moment()));
      } else if (value === "past_month") {
        form.change("fromDate", toIsoDateString(moment().subtract(1, "month")));
        form.change("toDate", toIsoDateString(moment()));
      } else if (value === "past_3_months") {
        form.change("fromDate", toIsoDateString(moment().subtract(3, "month").set({date: 1})));
        form.change("toDate", toIsoDateString(moment()));
      } else if (value === "past_6_months") {
        form.change("fromDate", toIsoDateString(moment().subtract(6, "month").set({date: 1})));
        form.change("toDate", toIsoDateString(moment()));
      } else if (value === "past_year") {
        form.change("fromDate", toIsoDateString(moment().subtract(1, "year")));
        form.change("toDate", toIsoDateString(moment()));
      } else if (value === "all") {
        form.change("fromDate", undefined);
        form.change("toDate", undefined);
      }
    });

  }

  const renderForm = (
      {handleSubmit, values, submitting, form}: FormRenderProps
  ): React.ReactNode => {

    return (
        <StyledForm className={props.className || ""}
                    onSubmit={handleSubmit}
        >

          <div className={"form-box"}
               onKeyDown={(e) => onEnterKeyPressed(
                   e, values as RequiresActionSearchParams)}>

            <StyledActionButton
                type="button"
                compact
                size="medium"
                onClick={() => doSearch(values as RequiresActionSearchParams)}
            >
              <Icon name="refresh"/>
              {getMessage("button.reload")}
            </StyledActionButton>

            <div className={"group-separator"}>&nbsp;</div>

            <div className={"label"}>
              {`${getMessage("from")}:`}
            </div>
            <div className={"value date"}>
              <Field
                  name="fromDate"
                  className="date"
                  component={DatePicker}
              />

              <div className={"label"}>
                {`${getMessage("to")}:`}
              </div>
              <Field
                  name="toDate"
                  className="date"
                  component={DatePicker}
              />

              <Dropdown
                  className={"time-frame-dropdown"}
                  inline
                  options={timeFrameOptions}
                  value={values.timeFrame}
                  onChange={(event, data) => {
                    onTimeFrameChange(data.value as string, form);
                  }}
              />
            </div>

            {
                props.displayRequiresAction
                && <>
                  <div className={"group-separator"}>&nbsp;</div>

                  <div className={"toggles"}>
                    <StyledActionButton
                        type="button"
                        compact
                        size="medium"
                        onClick={() => {
                          form.batch(() => {
                            const newVal = !values.requiresActionOnly;
                            form.change("requiresActionOnly", newVal);
                            form.change("requiresDoctorActionOnly", false);
                            form.change("abnormalFindingOnly", false);
                            form.change("discussWithPatientOnly", false);
                            form.change("noProcedureCreatedOnly", false);
                            form.change("procedureCreatedOnly", false);
                          });
                        }}
                    >
                      <Icon name={"exclamation circle"}
                            className={"toggle-icon"}
                            size={"large"}
                            color={values.requiresActionOnly ? "yellow" : undefined}
                      />
                    </StyledActionButton>

                    <StyledActionButton
                        type="button"
                        compact
                        size="medium"
                        onClick={() => {
                          form.batch(() => {
                            const newVal = !values.requiresDoctorActionOnly;
                            form.change("requiresActionOnly", false);
                            form.change("requiresDoctorActionOnly", newVal);
                            form.change("abnormalFindingOnly", false);
                            form.change("discussWithPatientOnly", false);
                            form.change("noProcedureCreatedOnly", false);
                            form.change("procedureCreatedOnly", false);
                          });
                        }}
                    >
                      <Icon name={values.requiresDoctorActionOnly ? "bell" : "bell outline"}
                            className={"toggle-icon"}
                            size={"large"}
                            color={values.requiresDoctorActionOnly ? "violet" : undefined}
                      />
                    </StyledActionButton>

                    <StyledActionButton
                        type="button"
                        compact
                        size="medium"
                        onClick={() => {
                          form.batch(() => {
                            const newVal = !values.abnormalFindingOnly;
                            form.change("requiresDoctorActionOnly", false);
                            form.change("requiresActionOnly", false);
                            form.change("abnormalFindingOnly", newVal);
                            form.change("discussWithPatientOnly", false);
                            form.change("noProcedureCreatedOnly", false);
                            form.change("procedureCreatedOnly", false);
                          });
                        }}
                    >
                      <Icon name={values.abnormalFindingOnly ? "flag" : "flag outline"}
                            className={"toggle-icon"}
                            size={"large"}
                            color={values.abnormalFindingOnly ? "red" : "grey"}
                      />
                    </StyledActionButton>

                    <StyledActionButton
                        type="button"
                        compact
                        size="medium"
                        onClick={() => {
                          form.batch(() => {
                            const newVal = !values.discussWithPatientOnly;
                            form.change("requiresActionOnly", false);
                            form.change("requiresDoctorActionOnly", false);
                            form.change("abnormalFindingOnly", false);
                            form.change("discussWithPatientOnly", newVal);
                            form.change("noProcedureCreatedOnly", false);
                            form.change("procedureCreatedOnly", false);
                          });
                        }}
                    >
                      <Icon name={values.discussWithPatientOnly ? "phone" : "phone"}
                            className={"toggle-icon"}
                            size={"large"}
                            color={values.discussWithPatientOnly ? "blue" : undefined}
                      />
                    </StyledActionButton>

                    <StyledActionButton
                        type="button"
                        compact
                        size="medium"
                        onClick={() => {
                          form.batch(() => {
                            const newVal = !values.procedureCreatedOnly;
                            form.change("requiresActionOnly", false);
                            form.change("requiresDoctorActionOnly", false);
                            form.change("abnormalFindingOnly", false);
                            form.change("discussWithPatientOnly", false);
                            form.change("noProcedureCreatedOnly", false);
                            form.change("procedureCreatedOnly", newVal);
                          });
                        }}
                    >
                      <Icon name={"dollar"}
                            className={"toggle-icon"}
                            size={"large"}
                            color={values.procedureCreatedOnly ? "blue" : undefined}
                      />
                    </StyledActionButton>

                    <StyledActionButton
                        type="button"
                        compact
                        size="medium"
                        onClick={() => {
                          form.batch(() => {
                            const newVal = !values.noProcedureCreatedOnly;
                            form.change("requiresActionOnly", false);
                            form.change("requiresDoctorActionOnly", false);
                            form.change("abnormalFindingOnly", false);
                            form.change("discussWithPatientOnly", false);
                            form.change("noProcedureCreatedOnly", newVal);
                            form.change("procedureCreatedOnly", false);
                          });
                        }}
                    >
                      <IconGroup className={"icon-group no-procedure-created"} size={"large"}>
                        <Icon name={"dont"}
                              className={"toggle-icon negation"}
                              size={"large"}
                              color={values.noProcedureCreatedOnly ? "blue" : undefined}
                        />
                        <Icon name={"dollar"}
                              className={"toggle-icon negated-icon"}
                              size={"small"}
                              color={values.noProcedureCreatedOnly ? "blue" : "grey"}
                        />
                      </IconGroup>
                    </StyledActionButton>

                  </div>
                </>
            }

            <FormSpy subscription={{values: true}} onChange={formState => handleChange(formState, form)}/>
          </div>
        </StyledForm>
    )
  };

  return (
      <FinalForm
          onSubmit={() => noop}
          initialValues={initialValues.current}
          subscription={{values: true, pristine: true}}
          render={renderForm}
      />
  );

}

export default withAuthContext(
    withMessages(
        RequiresActionSearchForm));
